import type { MedalTimes } from '../track/types';
import { shortLap } from './lapTime';

/**
 * Sharing a lap: a link that opens the game on that lap (watch it, then race
 * it), and the few lines that go with it in a chat. Copying works in one tap
 * on phones too; "send" opens the phone's share sheet (WhatsApp, Messages…).
 */

const ID_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

/** A new link id: 10 random letters and digits (made here, so the link can be copied in the same tap). */
export function newShareId(): string {
  const out: string[] = [];
  const bytes = new Uint8Array(16);
  while (out.length < 10) {
    crypto.getRandomValues(bytes);
    // 248 = 4 × 62: bytes above it would favour the first letters.
    for (const v of bytes) if (v < 248 && out.length < 10) out.push(ID_CHARS[v % 62]);
  }
  return out.join('');
}

/** The link to a shared lap: the game's own address with ?lap=. */
export function shareUrl(id: string, base = location.origin + location.pathname): string {
  return `${base}?lap=${id}`;
}

const MEDALS: [keyof MedalTimes, string][] = [
  ['bronze', '🥉'],
  ['silver', '🥈'],
  ['gold', '🥇'],
  ['author', '🏆'],
];
const NO_MEDAL = '▫️';

/** Every medal in a row, the ones this lap earned shown and the rest blank: 🥉🥈🥇▫️ */
export function medalStrip(lapMs: number, medals: MedalTimes): string {
  return MEDALS.map(([m, icon]) => (lapMs <= medals[m] ? icon : NO_MEDAL)).join('');
}

/** The best medal a lap earned (🏆 🥇 🥈 🥉), or ''. */
export function topMedal(lapMs: number, medals: MedalTimes): string {
  for (const [m, icon] of [...MEDALS].reverse()) if (lapMs <= medals[m]) return icon;
  return '';
}

export { shortLap };

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Tue 29 Sep" */
export function shortDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return `${DAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${d} ${MONTHS[m - 1]}`;
}

export interface ShareInfo {
  date: string;
  circuit: string;
  lapMs: number;
  medals: MedalTimes;
  /** Your place on the day's board, if you're on it. */
  rank: number | null;
  total: number | null;
  url: string;
  /** Sending it back after beating someone's lap: whose ('' for an unnamed one), and by how much. */
  beat?: { name: string; byMs: number } | null;
}

/**
 * 🏁 Grand Premio · Tue 29 Sep
 * 41.843 at Autodromo del Lago
 * 🥉🥈🥇▫️ · P3 of 27
 * Watch my lap, then beat it:
 * https://…/?lap=Ab3dE9xYz1
 */
export function shareText(o: ShareInfo): string {
  const beat = o.beat ? `, ${shortLap(o.beat.byMs)} faster than ${o.beat.name || 'yours'}` : '';
  return [
    `🏁 Grand Premio · ${shortDate(o.date)}`,
    `${shortLap(o.lapMs)} at ${o.circuit}${beat}`,
    medalStrip(o.lapMs, o.medals) + (o.rank !== null && o.total ? ` · P${o.rank} of ${o.total}` : ''),
    o.beat ? 'Your move:' : 'Can you beat it? Watch my lap, then race it:',
    o.url,
  ].join('\n');
}

/** Put text on the clipboard. Call it straight from the tap: phones only allow it then. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Refused (an old browser, or no longer inside the tap): try the old way.
  }
  return legacyCopy(text);
}

function legacyCopy(text: string): boolean {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  // Out of sight, selectable (the game turns selection off), and 16px so iOS doesn't zoom in on it.
  ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;font-size:16px;-webkit-user-select:text;user-select:text';
  document.body.appendChild(ta);
  ta.select();
  ta.setSelectionRange(0, text.length);
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  ta.remove();
  return ok;
}

/** The phone's own share sheet is there (WhatsApp, Messages…). */
export function canSend(): boolean {
  return typeof navigator.share === 'function';
}

/** Open the share sheet with the text (the link inside it: some apps drop a separate url). */
export async function sendText(text: string): Promise<'sent' | 'cancelled' | 'failed'> {
  try {
    await navigator.share({ text });
    return 'sent';
  } catch (e) {
    return (e as { name?: string })?.name === 'AbortError' ? 'cancelled' : 'failed';
  }
}
